import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { FloatingActionIslandContent } from '@/components/ui/FloatingActionIslandContent'

/**
 * The floating-island pager (as on the Directory tables), for server-paginated
 * lists: pages/window math runs on `total` + `pageSize` and `onPage` fetches.
 * Render inside a FloatingActionIslandProvider with <FloatingActionIsland />.
 */
export function FloatingPagination({
  componentId,
  itemType,
  total,
  page,
  pageSize,
  loading = false,
  onPage,
}: {
  componentId: string
  /** Plural noun for the count line, e.g. "events" */
  itemType: string
  total: number
  page: number
  pageSize: number
  loading?: boolean
  onPage: (page: number) => void
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const startIndex = (page - 1) * pageSize
  const goToPage = (target: number) => {
    if (loading || target < 1 || target > totalPages || target === page) return
    onPage(target)
  }

  return (
    <FloatingActionIslandContent
      show={totalPages > 1}
      componentId={componentId}
      priority={10}
    >
      <div className="inline-flex w-full lg:w-[520px] max-w-2xl">
        <div className="flex-grow items-center space-x-2 text-sm text-muted-foreground whitespace-nowrap pointer-events-none hidden md:flex">
          <span className="font-medium">
            <span className="mr-1">
              {total} {itemType}
            </span>
            <span className="text-muted-foreground/60 mx-1">•</span>
            <span className="ml-1">
              {startIndex + 1}-{Math.min(startIndex + pageSize, total)} of{' '}
              {total}
            </span>
          </span>
        </div>

        <div className="flex items-center space-x-2 w-[240px] justify-center md:justify-end pointer-events-auto">
          <div className="inline-flex items-center space-x-1">
            <button
              onClick={() => goToPage(page - 1)}
              disabled={loading || page === 1}
              className="pointer-events-auto relative inline-flex items-center justify-center w-10 h-10 text-sm font-medium text-muted-foreground bg-background/80 border border-border rounded-lg hover:bg-muted hover:border-border disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            <div className="inline-flex items-center space-x-1 justify-end">
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let pageNumber
                if (totalPages <= 5) {
                  pageNumber = i + 1
                } else if (page <= 3) {
                  pageNumber = i + 1
                } else if (page >= totalPages - 2) {
                  pageNumber = totalPages - 4 + i
                } else {
                  pageNumber = page - 2 + i
                }

                return (
                  <button
                    key={pageNumber}
                    onClick={() => goToPage(pageNumber)}
                    disabled={loading}
                    className={cn(
                      'pointer-events-auto relative inline-flex items-center justify-center w-10 h-10 text-sm font-medium border rounded-lg transition-all',
                      pageNumber === page
                        ? 'z-10 bg-black/90 text-white border-black'
                        : 'bg-background/80 border-border text-muted-foreground hover:bg-muted hover:border-border',
                    )}
                  >
                    {pageNumber}
                  </button>
                )
              })}
            </div>

            <button
              onClick={() => goToPage(page + 1)}
              disabled={loading || page === totalPages}
              className="pointer-events-auto relative inline-flex items-center justify-center w-10 h-10 text-sm font-medium text-muted-foreground bg-background/80 border border-border rounded-lg hover:bg-muted hover:border-border disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </FloatingActionIslandContent>
  )
}
